
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useInventoryStore } from "@/store/api/inventoryStore";
import { toast } from "sonner";

interface DeleteItemDialogProps {
  itemId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteItemDialog({ itemId, open, onOpenChange }: DeleteItemDialogProps) {
  const { t } = useTranslation(["inventory", "common"]);
  const { deleteItem, items } = useInventoryStore();
  const [isDeleting, setIsDeleting] = useState(false);

  const item = items.find(i => i.id === itemId);

  // Le store lève désormais quand la suppression échoue : le toast de succès
  // et la fermeture ne sont atteints que si l'écriture a réellement eu lieu.
  const handleDelete = async () => {
    if (!itemId) return;

    setIsDeleting(true);
    try {
      await deleteItem(itemId);
      toast.success(t('itemDeletedSuccess'));
      onOpenChange(false);
    } catch (error) {
      console.error('[DeleteItemDialog] deleteItem:', error);
      toast.error(t('itemDeletedError'));
    } finally {
      setIsDeleting(false);
    }
  };

  if (!item) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('deleteItem')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('deleteItemDescription', { item: `${item.name} (${item.size})` })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('cancel', { ns: 'common' })}</AlertDialogCancel>
          <AlertDialogAction
            // `preventDefault` : sans lui Radix ferme le dialogue au clic, y
            // compris quand la suppression échoue.
            onClick={(e) => { e.preventDefault(); handleDelete(); }}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {t('delete', { ns: 'common' })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
